import {
  CREATOR_MEMBERSHIP_TEASER_MAX_LENGTH,
  CreatorMembershipPostVisibility,
  type CreatorMembershipPostVisibilityValue,
} from './membership-types';

export type MembershipPostViewer = {
  isOwner?: boolean;
  entitled?: boolean;
  tierId?: string | null;
  tierSortOrder?: number | null;
};

export type MembershipPostAccessInput = {
  visibility: CreatorMembershipPostVisibilityValue;
  minTierId?: string | null;
  minTierSortOrder?: number | null;
};

export function canViewMembershipPost(
  post: MembershipPostAccessInput,
  viewer: MembershipPostViewer,
): boolean {
  if (viewer.isOwner) return true;
  if (post.visibility === CreatorMembershipPostVisibility.EVERYONE) return true;
  if (!viewer.entitled) return false;
  if (post.visibility === CreatorMembershipPostVisibility.FREE_PLUS) return true;
  if (post.visibility === CreatorMembershipPostVisibility.TIER_AND_ABOVE) {
    if (!post.minTierId || post.minTierSortOrder == null) return false;
    if (!viewer.tierId || viewer.tierSortOrder == null) return false;
    return viewer.tierSortOrder >= post.minTierSortOrder;
  }
  return false;
}

export type ValidateMembershipPostInput = {
  visibility: CreatorMembershipPostVisibilityValue;
  teaser?: string | null;
  minTierId?: string | null;
  forPublish?: boolean;
};

export function validateMembershipPostFields(input: ValidateMembershipPostInput): {
  ok: boolean;
  message?: string;
  normalized: {
    minTierId: string | null;
    teaser: string | null;
  };
} {
  const teaser = input.teaser?.trim() || null;
  const minTierId = input.minTierId?.trim() || null;

  if (teaser && teaser.length > CREATOR_MEMBERSHIP_TEASER_MAX_LENGTH) {
    return {
      ok: false,
      message: `Teaser must be ${CREATOR_MEMBERSHIP_TEASER_MAX_LENGTH} characters or fewer`,
      normalized: { minTierId, teaser },
    };
  }

  if (input.visibility === CreatorMembershipPostVisibility.EVERYONE) {
    return {
      ok: true,
      normalized: { minTierId: null, teaser: null },
    };
  }

  if (input.visibility === CreatorMembershipPostVisibility.FREE_PLUS) {
    if (input.forPublish && !teaser) {
      return {
        ok: false,
        message: `Gated posts require a teaser (max ${CREATOR_MEMBERSHIP_TEASER_MAX_LENGTH} characters).`,
        normalized: { minTierId: null, teaser },
      };
    }
    return {
      ok: true,
      normalized: { minTierId: null, teaser },
    };
  }

  if (input.visibility === CreatorMembershipPostVisibility.TIER_AND_ABOVE) {
    if (!minTierId) {
      return {
        ok: false,
        message: 'Select a minimum tier for tier-gated posts.',
        normalized: { minTierId: null, teaser },
      };
    }
    if (input.forPublish && !teaser) {
      return {
        ok: false,
        message: `Gated posts require a teaser (max ${CREATOR_MEMBERSHIP_TEASER_MAX_LENGTH} characters).`,
        normalized: { minTierId, teaser },
      };
    }
    return {
      ok: true,
      normalized: { minTierId, teaser },
    };
  }

  return {
    ok: false,
    message: 'Invalid post visibility',
    normalized: { minTierId, teaser },
  };
}

export type PublishMembershipPostInput = {
  membershipLive: boolean;
  publishedTierCount: number;
  visibility: CreatorMembershipPostVisibilityValue;
  teaser?: string | null;
  minTierId?: string | null;
};

export function canPublishMembershipPost(input: PublishMembershipPostInput): {
  ok: boolean;
  message?: string;
} {
  if (!input.membershipLive) {
    return {
      ok: false,
      message: 'Publishing requires a live membership. Go live from the workspace first.',
    };
  }
  if (input.publishedTierCount < 1) {
    return { ok: false, message: 'Publish at least one tier before publishing updates.' };
  }

  const fieldValidation = validateMembershipPostFields({
    visibility: input.visibility,
    teaser: input.teaser,
    minTierId: input.minTierId,
    forPublish: true,
  });
  if (!fieldValidation.ok) {
    return { ok: false, message: fieldValidation.message };
  }

  return { ok: true };
}
